import { useEffect, useRef, useState } from 'react';

/**
 * A three-dot menu for secondary actions on a card.
 *
 * Destructive actions used to sit on the card as their own button, which made
 * the most dangerous thing on screen also one of the most prominent. Tucking it
 * behind a menu is both tidier and better ordered: the actions you take daily
 * stay visible, the one you take once is a click away.
 *
 * Closes on outside click, on Escape, and after any item is chosen.
 */
export default function OverflowMenu({
  label = 'More actions',
  children,
}: {
  label?: string;
  children: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="overflow" ref={ref}>
      <button
        type="button"
        className="overflow-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
          <circle cx="8" cy="3" r="1.4" />
          <circle cx="8" cy="8" r="1.4" />
          <circle cx="8" cy="13" r="1.4" />
        </svg>
      </button>
      {open && (
        <div className="overflow-menu" role="menu">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
